import { FC, useEffect, useRef, useState } from 'react';

type CurrencyType = 'USD' | 'GBP' | 'EUR';

interface Props {
  /** Amount in subunits (e.g. cents). */
  amount?: number;
  fractionDigits?: number;
  currency?: CurrencyType;
  placement?: 'cart' | 'product';
  style?: Record<string, unknown>;
  classNames?: string;
}

/**
 * BNPL messaging for pages where Braintree also loads a PayPal SDK — the cart page and the cart
 * flyout.
 *
 * Why this exists, in full, because it is not obvious and was expensive to find:
 *
 * Braintree's express checkout loads its own PayPal SDK into the page and sets
 * `__PAYPAL_MANAGED_BY_BRAINTREE__`. From that moment PayPal's SDK refuses to install or re-render
 * a second messaging instance in that window. Verified in a browser against a real cart:
 *
 *   - mutating `data-pp-amount` on the container   -> ignored
 *   - rendering a second message into the wrapper  -> iframe never paints, promise never settles
 *   - clearing the container and rendering again   -> resolves, but redisplays the OLD amount
 *   - re-injecting our namespaced SDK script       -> the namespace never installs
 *   - rendering via Braintree's own paypal.Messages-> hangs, renders nothing
 *
 * The banner is correct on first paint only because our SDK loads before Braintree's, which is
 * exactly why a hard refresh shows the right figure and nothing after it does.
 *
 * So the message is rendered inside its own same-origin iframe. That child window has no Braintree
 * SDK in it, so PayPal behaves normally there. Because a *fresh window* is the only thing that
 * reliably produces a message at a new amount, each amount change builds a new iframe and swaps it
 * in once it reports a height — the old one stays visible until then, so there is no blank frame.
 *
 * The PDP deliberately does NOT use this: nothing loads Braintree there, so the simpler direct
 * render in PayPalBNPLMessages works and costs one fewer document.
 */

/** How long the amount must hold still before building an iframe for it. */
const SETTLE_DELAY_MS = 250;

const MESSAGE_EVENT = 'viewrail-bnpl-height';

const buildDocument = (
  clientId: string,
  amount: number,
  currency: CurrencyType,
  placement: string,
  style: Record<string, unknown>,
  frameId: string,
  parentOrigin: string,
) => {
  const sdkSrc =
    'https://www.paypal.com/sdk/js?' +
    new URLSearchParams({
      'client-id': clientId,
      components: 'messages',
      currency,
      'enable-funding': 'paylater',
    }).toString();

  // Every interpolated value is JSON-encoded: amount is numeric, the rest are our own config, but
  // encoding keeps a stray quote in a style value from breaking out of the script.
  return `<!doctype html>
<html>
  <head><meta charset="utf-8"><style>html,body{margin:0;padding:0;overflow:hidden;background:transparent}</style></head>
  <body>
    <div id="m"></div>
    <script src=${JSON.stringify(sdkSrc)}></script>
    <script>
      (function () {
        var FRAME_ID = ${JSON.stringify(frameId)};
        var EVENT = ${JSON.stringify(MESSAGE_EVENT)};
        // A srcdoc document's own origin is the opaque string "null", so it cannot address the
        // parent with window.location.origin — the real parent origin has to be handed in.
        var PARENT_ORIGIN = ${JSON.stringify(parentOrigin)};
        // Report only genuine height changes, and never more than a handful of times.
        //
        // The parent resizes this iframe to the height we report, which resizes our own viewport,
        // which fires the observer again. Reporting unconditionally is therefore an infinite
        // feedback loop that pegs the renderer — it froze the page and starved the express-checkout
        // buttons that initialise after this component.
        var lastHeight = -1;
        var reports = 0;
        var MAX_REPORTS = 10;
        function report() {
          if (reports >= MAX_REPORTS) return;
          // Measure the message element only — never the document, whose scroll height tracks the
          // viewport the parent just sized and would reintroduce the feedback loop.
          var h = document.getElementById('m').offsetHeight || 0;
          if (h <= 0) return;
          if (h === lastHeight) return;
          lastHeight = h;
          reports++;
          parent.postMessage({ type: EVENT, frameId: FRAME_ID, height: h }, PARENT_ORIGIN);
        }
        function fail() { parent.postMessage({ type: EVENT, frameId: FRAME_ID, height: 0 }, PARENT_ORIGIN); }
        if (!window.paypal || !window.paypal.Messages) { fail(); return; }
        try {
          window.paypal.Messages({
            amount: ${JSON.stringify(amount)},
            currency: ${JSON.stringify(currency)},
            placement: ${JSON.stringify(placement)},
            style: ${JSON.stringify(style)}
          }).render('#m').then(report).catch(fail);
        } catch (e) { fail(); }
        // Watch the message element, not document.body: body height follows the iframe viewport,
        // which the parent sets from what we report, so observing it is inherently circular. The
        // message element's height is driven by its own content.
        if (window.ResizeObserver) new ResizeObserver(report).observe(document.getElementById('m'));
        setTimeout(function () {
          if (lastHeight <= 0) fail();
        }, 5000);
      })();
    </script>
  </body>
</html>`;
};

let frameSequence = 0;

const PayPalBNPLIsolatedMessage: FC<Props> = ({
  amount,
  fractionDigits = 2,
  currency = 'USD',
  placement = 'cart',
  style = { layout: 'text', logo: { type: 'primary' } },
  classNames = '',
}) => {
  const clientId = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID;
  const wrapperRef = useRef<HTMLDivElement>(null);
  /** The iframe currently on screen. Kept until its replacement reports a height. */
  const activeFrameRef = useRef<HTMLIFrameElement | null>(null);
  const [minHeight, setMinHeight] = useState(0);

  const numericAmount = amount !== undefined ? amount / Math.pow(10, fractionDigits) : undefined;
  const styleKey = JSON.stringify(style);

  useEffect(() => {
    if (!clientId || numericAmount === undefined || numericAmount <= 0) return;

    let cancelled = false;
    let frame: HTMLIFrameElement | null = null;
    const frameId = `bnpl-${++frameSequence}`;

    const onMessage = (event: MessageEvent) => {
      // Identity, not origin: a srcdoc document reports its origin as the opaque string "null", so
      // an origin comparison can never authenticate it. Requiring the event to come from this
      // exact iframe's window is stricter anyway — no other frame can forge it.
      if (cancelled || !frame || event.source !== frame.contentWindow) return;
      const data = event.data as { type?: string; frameId?: string; height?: number };
      if (data?.type !== MESSAGE_EVENT || data.frameId !== frameId) return;

      const height = Math.max(0, Math.round(data.height ?? 0));
      if (height <= 0) {
        // PayPal declined to serve a message for this amount (eligibility). Drop the new frame and
        // keep whatever is already on screen rather than blanking the banner.
        if (frame !== activeFrameRef.current) frame.remove();
        return;
      }

      frame.style.height = `${height}px`;
      frame.style.position = '';
      frame.style.top = '';
      frame.style.left = '';
      frame.style.visibility = '';

      const previous = activeFrameRef.current;
      if (previous && previous !== frame) previous.remove();
      activeFrameRef.current = frame;
      setMinHeight((prev) => (height > prev ? height : prev));
    };

    const settle = setTimeout(() => {
      const wrapper = wrapperRef.current;
      if (cancelled || !wrapper) return;

      frame = document.createElement('iframe');
      frame.setAttribute('title', 'PayPal Pay Later messaging');
      frame.setAttribute('scrolling', 'no');
      frame.style.width = '100%';
      frame.style.border = '0';
      frame.style.display = 'block';
      frame.style.height = '0px';
      // Build behind the current message so the swap has no blank frame.
      if (activeFrameRef.current) {
        frame.style.position = 'absolute';
        frame.style.top = '0';
        frame.style.left = '0';
        frame.style.visibility = 'hidden';
      }
      frame.srcdoc = buildDocument(
        clientId,
        numericAmount,
        currency,
        placement,
        JSON.parse(styleKey),
        frameId,
        window.location.origin,
      );
      wrapper.appendChild(frame);
      window.addEventListener('message', onMessage);
    }, SETTLE_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(settle);
      window.removeEventListener('message', onMessage);
      // Only tear down a frame that never became the visible one; the active frame is removed by
      // its own replacement, so the banner never flickers between renders.
      if (frame && frame !== activeFrameRef.current) frame.remove();
    };
  }, [clientId, numericAmount, currency, placement, styleKey]);

  // Remove the surviving iframe when the component itself goes away (flyout close, navigation).
  useEffect(
    () => () => {
      activeFrameRef.current?.remove();
      activeFrameRef.current = null;
    },
    [],
  );

  if (!clientId) return null;

  return (
    <div
      ref={wrapperRef}
      className={`paypal-bnpl-messaging ${classNames}`}
      style={{ position: 'relative', minHeight: minHeight ? `${minHeight}px` : undefined }}
    />
  );
};

export default PayPalBNPLIsolatedMessage;
